import React from 'react';
import PropTypes from 'prop-types';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import _capitalize from 'lodash/capitalize';
import _cloneDeep from 'lodash/cloneDeep';
import { useSelector } from 'react-redux';
import _get from 'lodash/get';
import _has from 'lodash/has';
import _isEmpty from 'lodash/isEmpty';
import _isUndefined from 'lodash/isUndefined';
import { makeStyles } from '@material-ui/core/styles';
import Avatar from '@material-ui/core/Avatar';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import {
  makeSelectAllowSignIn,
  makeSelectClientDetails,
} from 'containers/Main/selectors';
import {
  makeSelectRecommendedResources,
  makeSelectRelatedNews,
} from 'containers/Resources/selectors';
import DetailImageAndLabel from 'components/DetailImageAndLabel';
import Divider from 'components/Divider';
import LabelValue from 'components/LabelValue';
import PageIntroBlock from 'components/PageIntroBlock';
import ItemPageDescriptionContainer from 'components/ItemPageDescriptionContainer';
import ItemPageScoring from 'components/ItemPageScoring';
import ResourceEvidence from 'components/ResourceEvidence';
import ResourceName from 'components/ResourceName';
import ResourcePageActions from 'components/ResourcePageActions';
import ResourcePageHighlights from 'components/ResourcePageHighlights';
import ResourceType from 'components/ResourceType';
import RichTextReactRenderer from 'components/RichTextReactRenderer';
import Hidden from '@material-ui/core/Hidden';
import MobileTaggedTopics from 'components/ResourcePageLeftPanel/MobileTaggedTopics';
import MobileActionsPanel from 'components/ResourcePageLeftPanel/MobileActionsPanel';
import PaddedGrid from 'components/PaddedGrid';
import { getImageFile } from 'utils/stringUtils';
import ResourceImageAndInfoLayout from 'components/ResourceImageAndInfoLayout';
import Box from '@material-ui/core/Box';
import useSiteCopySelector from 'components/useSiteCopySelector';
import useDateLocale from 'components/useDateLocale';
import Editor from './Editor';
import { buildRichTextLink, getBlogType } from '../utils';
import ArticleLinks from './ArticleLinks';

const useStyles = makeStyles(theme => ({
  shortDescription: {
    textAlign: 'left',
    fontSize: '0.875rem',
  },
  guestWrapper: {
    alignItems: 'center',
    display: 'flex',
  },
  guestImage: {
    width: 32,
    height: 32,
    marginLeft: 5,
  },
  divider: {
    borderRight: '1px solid #E9ECED',
    margin: '0 15px',
    height: 13,
  },
  guestLabel: {
    ...theme.typography.pxSmall,
    marginRight: 10,
    marginTop: 3,
  },
}));

function ReadInformation(props) {
  const {
    data,
    isCredibleBlog,
    leftPanelData,
    hasUserReviews,
    resourcePageActionsProps,
    renderLinkNodeEntry,
    linkAsPlainText,
    showMobileNode = true,
  } = props;

  const classes = useStyles();
  const recommendedResources = useSelector(makeSelectRecommendedResources());
  const relatedNews = useSelector(makeSelectRelatedNews());
  const allowSignIn = useSelector(makeSelectAllowSignIn());
  const clientDetails = useSelector(makeSelectClientDetails());
  const [recommendBotSiteCopy, resourceItemPageSiteCopy] = useSiteCopySelector([
    'recommendbot-a',
    'resource-item-page',
  ]);
  const dateFormatFilter = useDateLocale();
  if (_isEmpty(data)) return null;
  const {
    sys: { id, contentType },
    fields: {
      name,
      slug,
      person = [],
      description = '',
      audienceType = [],
      highlights = [],
      tags = [],
      shortDescription = '',
      url = '',
      organization = [],
      publicationDate = '',
      source = [],
      editors = [],
      blog,
      type,
      client,
      internalStatusIgnore = [],
      topics,
    },
    fields,
  } = data[0];
  const isNews = type === 'News';
  const orgsSeparator = !_isEmpty(_get(person, '0.fields'))
    ? ','
    : _get(resourceItemPageSiteCopy, 'pageCopy.articlePeopleOrgsOwnerLabel');
  const blogType = getBlogType(fields);
  const isGuestBlog = isCredibleBlog && blogType === 'guest';
  const guestImage = isGuestBlog && getImageFile(person[0].fields);
  const isClientOnly = !_isEmpty(_get(client, '0.fields'));

  const stopRecommendBot = internalStatusIgnore.includes('Stop RecommendBot');
  const defaultParagraph = _get(recommendBotSiteCopy, 'body.json');
  const finalDescription = _cloneDeep(description);
  if (
    !stopRecommendBot &&
    !_isEmpty(_get(leftPanelData, 'acceptedDeepLinkTopics')) &&
    defaultParagraph &&
    _get(finalDescription, 'content', null) &&
    !_get(finalDescription, 'content', '').includes(defaultParagraph)
  ) {
    if (url && !isCredibleBlog)
      finalDescription.content = finalDescription.content.concat(
        buildRichTextLink(
          url,
          _get(resourceItemPageSiteCopy, 'pageCopy.articleReadFullLabel'),
        ),
      );
    finalDescription.content = finalDescription.content.concat(
      defaultParagraph,
    );
  }
  const recommendBotConfig = _get(recommendBotSiteCopy, 'pageCopy');
  const clientsAssociated = isClientOnly
    ? client.map(({ fields: { shortName } }) => shortName)
    : [];
  const brand = _get(clientDetails, 'shortName', '');
  const hidePageScoring = isClientOnly && clientsAssociated.includes(brand);
  const imageNode = (
    <DetailImageAndLabel
      data={fields}
      type={contentType.sys.id}
      render={() => <ArticleLinks data={data} />}
      withLinks={!_isUndefined(url)}
    />
  );
  const mobileNode = showMobileNode ? (
    <Hidden mdUp>
      <MobileTaggedTopics data={_get(leftPanelData, 'acceptedTopics')} />
    </Hidden>
  ) : null;
  const infoNode = (
    <>
      <PaddedGrid item xs={12}>
        <ResourceType
          type={
            isCredibleBlog
              ? _get(resourceItemPageSiteCopy, [
                  'pageCopy',
                  'resourceTypeMapping',
                  'BLOG',
                ])
              : isNews
              ? _get(resourceItemPageSiteCopy, [
                  'pageCopy',
                  'resourceTypeMapping',
                  'NEWS',
                ])
              : _get(resourceItemPageSiteCopy, [
                  'pageCopy',
                  'resourceTypeMapping',
                  'ARTICLE',
                ])
          }
        />
      </PaddedGrid>
      <PaddedGrid item xs={12}>
        <Box
          component="div"
          display="flex"
          justifyContent="space-between"
          width="100%"
        >
          <ResourceName name={name} isBig />
          {allowSignIn && resourcePageActionsProps && (
            <ResourcePageActions
              key={_get(resourcePageActionsProps, 'id')}
              {...resourcePageActionsProps}
            />
          )}
        </Box>
      </PaddedGrid>
      {isGuestBlog && (
        <Grid item xs={12} className={classes.guestWrapper}>
          <LabelValue
            label={_get(
              resourceItemPageSiteCopy,
              'pageCopy.articlePeopleOrgsOwnerLabel',
            )}
            value={person}
          />
          <Typography className={classes.guestLabel} color="textPrimary">
            , {_get(resourceItemPageSiteCopy, 'pageCopy.articleGuestBlogLabel')}
          </Typography>
          <Avatar
            src={guestImage}
            className={classes.guestImage}
            alt="Blog Author"
          />
        </Grid>
      )}
      {!isGuestBlog && isCredibleBlog && (
        <Editor
          articleId={id}
          articleName={name}
          data={editors}
          publicationDate={publicationDate}
        />
      )}
      {!isCredibleBlog && (
        <>
          {shortDescription && (
            <Grid item xs={12}>
              <Typography className={classes.shortDescription}>
                {shortDescription}
              </Typography>
            </Grid>
          )}
          {!isNews ? (
            <PaddedGrid item xs={12} style={{ alignItems: 'center' }}>
              <LabelValue
                label={_get(
                  resourceItemPageSiteCopy,
                  'pageCopy.articlePeopleOrgsOwnerLabel',
                )}
                value={person}
              />
              <LabelValue
                label={orgsSeparator}
                value={organization}
                type="organizations"
              />
            </PaddedGrid>
          ) : (
            <PaddedGrid item xs={12} style={{ alignItems: 'center' }}>
              {!_isEmpty(publicationDate) && (
                <Typography variant="overline" color="textPrimary">
                  {format(parseISO(publicationDate), 'MMM dd, yyyy', {
                    locale: dateFormatFilter,
                  })}
                </Typography>
              )}
              {!_isEmpty(person) && <div className={classes.divider} />}
              <LabelValue
                label={`${_capitalize(
                  _get(
                    resourceItemPageSiteCopy,
                    'pageCopy.articlePeopleOrgsOwnerLabel',
                    '',
                  ),
                )}:`}
                value={person}
              />
              {!_isEmpty(organization) && <div className={classes.divider} />}
              <LabelValue value={organization} type="organizations" />
            </PaddedGrid>
          )}
        </>
      )}
      {!_isEmpty(publicationDate) && !isNews && isGuestBlog && (
        <PaddedGrid item xs={12}>
          <Typography>
            {format(parseISO(publicationDate), 'MMM dd, yyyy')}
          </Typography>
        </PaddedGrid>
      )}
      {!isCredibleBlog && !isNews && !hidePageScoring && (
        <ItemPageScoring
          data={data}
          hideExpertRating={isClientOnly}
          hasUserLink={hasUserReviews}
        />
      )}
      {showMobileNode && (
        <Hidden mdUp>
          <MobileActionsPanel data={leftPanelData} />
        </Hidden>
      )}
      <ResourcePageHighlights
        audience={audienceType}
        highlights={highlights}
        tags={tags}
      />
    </>
  );
  return (
    <>
      <ResourceImageAndInfoLayout
        image={imageNode}
        mobile={mobileNode}
        info={infoNode}
      />
      <Grid item xs={12}>
        <PageIntroBlock type="reference" slug={slug} page="activity" />
      </Grid>
      <ItemPageDescriptionContainer>
        <RichTextReactRenderer
          renderLinkNodeEntry={renderLinkNodeEntry}
          resourceData={data[0]}
          data={finalDescription}
          isDescription
          type="name"
          name={name}
          contentType="article"
          blog={blog}
          subtype={type}
          extraData={{
            stopRecommendBot,
            recommendBotConfig,
            resourceType: stopRecommendBot
              ? []
              : recommendedResources.filter(el => el.type !== 'Assessments'),
            assessments: stopRecommendBot
              ? []
              : recommendedResources.filter(el => el.type === 'Assessments'),
            topics:
              stopRecommendBot || !topics
                ? []
                : topics.filter(
                    el =>
                      !el.approach &&
                      _has(el, 'fields.reviewStatus') &&
                      el.fields.reviewStatus !== 'Internal',
                  ),
            approaches:
              stopRecommendBot || !topics
                ? []
                : topics.filter(el => el.approach),
            news: stopRecommendBot ? [] : relatedNews,
          }}
          linkAsPlainText={linkAsPlainText}
        />
      </ItemPageDescriptionContainer>
      {url && !isCredibleBlog && (
        <>
          <PaddedGrid item xs={12}>
            <Divider top={20} bottom={20} />
          </PaddedGrid>
          <ResourceEvidence
            data={source}
            title={_get(
              resourceItemPageSiteCopy,
              'pageCopy.articleEvidenceLabel',
              '',
            )}
          />
          {!_isEmpty(data) && (
            <ArticleLinks
              data={data}
              section="page"
              organization={organization}
            />
          )}
        </>
      )}
    </>
  );
}

ReadInformation.propTypes = {
  data: PropTypes.array,
  isAdmin: PropTypes.bool,
  save: PropTypes.func,
  saved: PropTypes.bool,
  isCredibleBlog: PropTypes.bool,
  hasUserReviews: PropTypes.bool,
  rate: PropTypes.func,
  review: PropTypes.object,
  sendReview: PropTypes.func,
  leftPanelData: PropTypes.object,
};

export default ReadInformation;
